/**
 * The vocabulary every bank-export converter shares: how to read the values a
 * bank writes, and how to report what could not be read.
 *
 * Amounts and dates are the two fields every importer has to guess at, and
 * they are guessed the same way whether the row came from a CSV, an OFX
 * `<TRNAMT>`, or a QIF `T` line — `1.234` is a thousand-and-something in
 * Berlin either way, so the rules live here rather than in whichever converter
 * needed them first.
 *
 * Pure and dependency-free: the tools that use it run entirely in the browser.
 */

/**
 * Something a converter could not represent, tied to where it was found.
 *
 * One shape for all three converters: they each reported the same thing under
 * a different name (`ConversionNote`, `ParseIssue`, `RowError { reason }`),
 * which is what stopped them sharing an output panel.
 *
 * A note is a stable `code` plus typed `args`, with the English `message`
 * derived from the same pair via {@link formatDiagnostic} — never the other
 * way round. Localizing a diagnostic means switching on the code (see the
 * tools output UI), not parsing the English sentence. CSV keeps its own
 * `RowError { reason }` schema and is untouched by this.
 */
export type ConversionNote = ConversionDiagnostic & {
  /** 1-based line in the source, or 0 when the note is about the file. */
  line: number;
  /** English fallback rendered from `code`/`args`; the UI may replace it. */
  message: string;
};

/**
 * Machine-readable kind of a date failure, shared by every converter that
 * reads dates through {@link normalizeDateDetailed} or {@link isoDate}.
 */
export type DateErrorKind =
  | "empty"
  | "out-of-range"
  | "unknown-month"
  | "unrecognized"
  | "unreadable";

/** English text for each {@link DateErrorKind} (fallback rendering only). */
const DATE_ERROR_MESSAGE: Record<DateErrorKind, string> = {
  empty: "empty date",
  "out-of-range": "out-of-range date",
  "unknown-month": "unknown month",
  unrecognized: "unrecognized date format",
  unreadable: "unreadable date",
};

/**
 * One diagnosable converter problem: a stable code, its arguments, and the
 * severity that code always carries. The union is the registry — adding a
 * producer-side message without adding a member here is a type error at the
 * `makeConversionNote` call, and passing args that do not belong to the code
 * is a type error too.
 */
/** What an OFX correction record asks for; anything the spec does not name is `unknown`. */
export type OfxCorrectionAction = "replace" | "delete" | "unknown";

export type ConversionDiagnostic =
  | {
      severity: "error";
      code: "import.unknown-format";
      args: Record<string, never>;
    }
  | {
      severity: "error";
      code: "import.missing-date-or-amount";
      args: Record<string, never>;
    }
  | {
      severity: "error";
      code: "import.unreadable-amount";
      args: { raw: string };
    }
  | {
      severity: "error";
      code: "import.unreadable-date";
      args: { raw: string; kind: DateErrorKind };
    }
  | {
      severity: "error";
      code: "import.invalid-account";
      args: { account: string };
    }
  | {
      severity: "error";
      code: "import.invalid-currency";
      args: { currency: string };
    }
  | {
      severity: "error";
      code: "ofx.no-transactions";
      args: Record<string, never>;
    }
  | {
      severity: "warning";
      code: "ofx.missing-currency";
      args: Record<string, never>;
    }
  | {
      severity: "error";
      code: "ofx.multiple-statements";
      args: Record<string, never>;
    }
  | {
      severity: "error";
      code: "ofx.unsupported-statement";
      args: { kind: string };
    }
  | {
      severity: "error";
      code: "ofx.orphan-transaction";
      args: Record<string, never>;
    }
  | {
      severity: "error";
      code: "ofx.correction-unsupported";
      args: { action: OfxCorrectionAction; target: string };
    }
  | {
      severity: "warning";
      code: "qif.split";
      args: Record<string, never>;
    }
  | {
      severity: "error";
      code: "qif.no-transactions";
      args: Record<string, never>;
    }
  | {
      severity: "error";
      code: "qif.split-malformed";
      args: {
        field: "S" | "E" | "$" | "%";
        reason: "orphan" | "duplicate" | "too-many";
      };
    }
  | {
      severity: "error";
      code: "qif.split-missing-amount";
      args: { category: string };
    }
  | {
      severity: "error";
      code: "qif.split-percentage-only";
      args: { category: string };
    }
  | {
      severity: "error";
      code: "qif.split-sum-mismatch";
      args: { total: string; sum: string };
    }
  | {
      severity: "error";
      code: "qif.split-unmapped";
      args: { category: string };
    }
  | {
      severity: "error";
      code: "qif.conflicting-amount";
      args: { first: string; second: string };
    }
  | {
      severity: "error";
      code: "qif.unsupported-record";
      args: { type: string };
    }
  | {
      severity: "error";
      code: "qif.multiple-accounts";
      args: { sections: number };
    }
  | {
      severity: "error";
      code: "ledger.include";
      args: { line: string };
    }
  | {
      severity: "error";
      code: "ledger.unsupported-transaction";
      args: { kind: "=" | "~" };
    }
  | {
      severity: "warning";
      code: "ledger.unsupported-directive";
      args: { directive: string };
    }
  | {
      severity: "error";
      code: "ledger.bad-date";
      args: { raw: string };
    }
  | {
      severity: "error";
      code: "ledger.virtual-posting";
      args: Record<string, never>;
    }
  | {
      severity: "error";
      code: "ledger.unknown-root";
      args: { raw: string; account: string };
    }
  | {
      severity: "warning";
      code: "ledger.account-renamed";
      args: { raw: string; account: string };
    }
  | {
      severity: "error";
      code: "ledger.bad-amount";
      args: { raw: string };
    }
  | {
      severity: "warning";
      code: "ledger.unrecognized-line";
      args: { line: string };
    };

/** Every diagnostic code, for exhaustiveness checks and the future UI map. */
export const CONVERSION_CODES: readonly ConversionCode[] = [
  "import.unknown-format",
  "import.missing-date-or-amount",
  "import.unreadable-amount",
  "import.unreadable-date",
  "import.invalid-account",
  "import.invalid-currency",
  "ofx.no-transactions",
  "ofx.missing-currency",
  "ofx.multiple-statements",
  "ofx.unsupported-statement",
  "ofx.orphan-transaction",
  "ofx.correction-unsupported",
  "qif.split",
  "qif.no-transactions",
  "qif.split-malformed",
  "qif.split-missing-amount",
  "qif.split-percentage-only",
  "qif.split-sum-mismatch",
  "qif.split-unmapped",
  "qif.conflicting-amount",
  "qif.unsupported-record",
  "qif.multiple-accounts",
  "ledger.include",
  "ledger.unsupported-transaction",
  "ledger.unsupported-directive",
  "ledger.bad-date",
  "ledger.virtual-posting",
  "ledger.unknown-root",
  "ledger.account-renamed",
  "ledger.bad-amount",
  "ledger.unrecognized-line",
];

/** Every code in {@link ConversionDiagnostic}. */
export type ConversionCode = ConversionDiagnostic["code"];

/** The argument shape belonging to one code; anything else is rejected. */
export type ConversionArgs<C extends ConversionCode> = Extract<
  ConversionDiagnostic,
  { code: C }
>["args"];

const CONVERSION_SEVERITY: {
  [C in ConversionCode]: Extract<
    ConversionDiagnostic,
    { code: C }
  >["severity"];
} = {
  "import.unknown-format": "error",
  "import.missing-date-or-amount": "error",
  "import.unreadable-amount": "error",
  "import.unreadable-date": "error",
  "import.invalid-account": "error",
  "import.invalid-currency": "error",
  "ofx.no-transactions": "error",
  "ofx.missing-currency": "warning",
  "ofx.multiple-statements": "error",
  "ofx.unsupported-statement": "error",
  "ofx.orphan-transaction": "error",
  "ofx.correction-unsupported": "error",
  "qif.split": "warning",
  "qif.no-transactions": "error",
  "qif.split-malformed": "error",
  "qif.split-missing-amount": "error",
  "qif.split-percentage-only": "error",
  "qif.split-sum-mismatch": "error",
  "qif.split-unmapped": "error",
  "qif.conflicting-amount": "error",
  "qif.unsupported-record": "error",
  "qif.multiple-accounts": "error",
  "ledger.include": "error",
  "ledger.unsupported-transaction": "error",
  "ledger.unsupported-directive": "warning",
  "ledger.bad-date": "error",
  "ledger.virtual-posting": "error",
  "ledger.unknown-root": "error",
  "ledger.account-renamed": "warning",
  "ledger.bad-amount": "error",
  "ledger.unrecognized-line": "warning",
};

/**
 * English fallback for a diagnostic, derived from its code and args. This is
 * what {@link ConversionNote.message} carries so existing verbatim rendering
 * keeps working; localized UI must switch on the code instead.
 */
export function formatDiagnostic(diagnostic: ConversionDiagnostic): string {
  switch (diagnostic.code) {
    case "import.unknown-format":
      return "not an OFX or QIF file — OFX starts with an OFXHEADER or <OFX>, QIF with a !Type: line";
    case "import.missing-date-or-amount":
      return "transaction is missing its date or amount";
    case "import.unreadable-amount":
      return `unreadable amount: ${diagnostic.args.raw}`;
    case "import.unreadable-date":
      return `${DATE_ERROR_MESSAGE[diagnostic.args.kind]}: ${diagnostic.args.raw}`;
    case "import.invalid-account":
      return `invalid account: ${diagnostic.args.account}`;
    case "import.invalid-currency":
      return `invalid currency: ${diagnostic.args.currency}`;
    case "ofx.missing-currency":
      return "no currency reported — amounts are labeled USD; confirm the statement's currency before filing";
    case "ofx.no-transactions":
      return "no <STMTTRN> transactions found — is this a statement export?";
    case "ofx.multiple-statements":
      return "multiple statements found — select one statement to convert; nothing was merged";
    case "ofx.unsupported-statement":
      return `unsupported ${diagnostic.args.kind} statement — only bank and credit-card statements convert; it was left out`;
    case "ofx.orphan-transaction":
      return "transaction outside any statement — it was left out; check the file's statement boundaries";
    case "ofx.correction-unsupported": {
      const { action, target } = diagnostic.args;
      const what =
        action === "delete"
          ? "deletion"
          : action === "replace"
            ? "replacement"
            : "correction";
      const of = target ? ` of transaction ${target}` : "";
      return `bank ${what}${of} is not applied — it was left out; fix the earlier entry in your ledger by hand`;
    }
    case "qif.split":
      return "split transaction — choose an account for each split category in the split review; it converts once every part has one";
    case "qif.no-transactions":
      return "no transactions found — is this a QIF export?";
    case "qif.split-malformed": {
      const { field, reason } = diagnostic.args;
      if (reason === "too-many") {
        return "more than 250 split lines in one transaction — it was left out";
      }
      return reason === "orphan"
        ? `split field ${field} appears before any S category line — the transaction was left out; check the split's field order`
        : `split field ${field} repeats within one split — the transaction was left out; each S line takes at most one E, $ and % line`;
    }
    case "qif.split-missing-amount":
      return `split "${diagnostic.args.category}" has no $ amount — the transaction was left out; re-export with split amounts`;
    case "qif.split-percentage-only":
      return `split "${diagnostic.args.category}" gives only a percentage — percentage splits are not converted; re-export with $ amounts`;
    case "qif.split-sum-mismatch":
      return `split amounts add up to ${diagnostic.args.sum}, not the transaction total ${diagnostic.args.total} — the transaction was left out; correct the splits and re-export`;
    case "qif.split-unmapped":
      return `split "${diagnostic.args.category}" has no valid destination account — the transaction was left out; map the category in the split review`;
    case "qif.conflicting-amount":
      return `conflicting amounts ${diagnostic.args.first} and ${diagnostic.args.second} for one transaction — it was left out`;
    case "qif.unsupported-record":
      return `record in an unsupported !Type:${diagnostic.args.type} section — only bank, cash, credit-card and other asset/liability registers convert; it was left out`;
    case "qif.multiple-accounts":
      return `transactions for ${diagnostic.args.sections} accounts found — export one account's register at a time; nothing was converted`;
    case "ledger.include":
      return `\`include\` is not supported — inline the file first: ${diagnostic.args.line}`;
    case "ledger.unsupported-transaction":
      return diagnostic.args.kind === "="
        ? "automated transaction (`=`) has no Beancount equivalent — rewrite as a plugin or drop it"
        : "periodic transaction (`~`) has no Beancount equivalent — use a recurring entry or drop it";
    case "ledger.unsupported-directive":
      return `\`${diagnostic.args.directive}\` directive has no direct equivalent — review by hand`;
    case "ledger.bad-date":
      return `unrecognized date "${diagnostic.args.raw}"`;
    case "ledger.virtual-posting":
      return "virtual posting — Beancount has no equivalent, so it is commented out (a virtual posting does not balance)";
    case "ledger.unknown-root":
      return `account "${diagnostic.args.raw}" has no Beancount root — filed under ${diagnostic.args.account}, re-file it`;
    case "ledger.account-renamed":
      return `account "${diagnostic.args.raw}" renamed to ${diagnostic.args.account} (Beancount needs capitalized, space-free segments)`;
    case "ledger.bad-amount":
      return `could not read amount "${diagnostic.args.raw}"`;
    case "ledger.unrecognized-line":
      return `unrecognized line — left as a comment: ${diagnostic.args.line}`;
    default: {
      const _exhaustive: never = diagnostic;
      throw new Error(`unknown conversion code: ${JSON.stringify(_exhaustive)}`);
    }
  }
}

/**
 * Build a {@link ConversionNote} from a code and its args. Severity and the
 * English message follow from the pair, so producers cannot mismatch them —
 * and a wrong arg shape for the code fails typecheck at the call.
 */
export function makeConversionNote<C extends ConversionCode>(
  line: number,
  code: C,
  args: ConversionArgs<C>,
): ConversionNote {
  // One contained cast: callers are checked, but TS cannot correlate two
  // separate generic parameters back into the discriminated union itself.
  const diagnostic = { code, args, severity: CONVERSION_SEVERITY[code] } as ConversionDiagnostic;
  return { line, message: formatDiagnostic(diagnostic), ...diagnostic };
}

/**
 * True for a misconfigured setup note (`import.invalid-account`,
 * `import.invalid-currency`): the emitter produced no output at all, so the
 * panel shows setup guidance and analytics reports `blocked`. Every other
 * error is per-record — the valid records still convert as partial output.
 * One predicate so the panel and the event can never disagree about it.
 */
export function isBlockingSetupNote(note: ConversionNote): boolean {
  return (
    note.code === "import.invalid-account" ||
    note.code === "import.invalid-currency"
  );
}

/** Which way round a two-part numeric date reads. */
export type DateOrder = "mdy" | "dmy";

/** Amount plus the precision it was written with. */
export interface ParsedAmount {
  value: number;
  decimals: number;
}

/**
 * Parse an amount, keeping the precision it was written with.
 *
 * Deciding which separator is the decimal point is the whole problem, since
 * `1.234` means a thousand-and-something in Berlin and one-point-something in
 * Boston. The rules, in order:
 *  - both `.` and `,` present → the rightmost is the decimal point
 *    (`1.234,56` and `1,234.56` both read as 1234.56);
 *  - one separator type, appearing more than once → it groups thousands
 *    (`1.234.567`);
 *  - one separator, exactly 3 digits after → thousands for `,` (`1,234`),
 *    decimal for `.` (the common export style);
 *  - otherwise → decimal, which is what keeps `0.12345678` intact.
 */
export function parseAmount(raw: string): ParsedAmount | null {
  let s = (raw ?? "").trim();
  if (s === "") return null;

  let negative = false;
  if (/^\(.*\)$/.test(s)) {
    negative = true;
    s = s.slice(1, -1);
  }
  // Strip currency symbols, codes and spaces (incl. NBSP thousands separators).
  s = s.replace(/[^\d,.\-+]/g, "");
  if (s.endsWith("-")) {
    negative = true;
    s = s.slice(0, -1);
  }
  if (s.startsWith("-")) {
    negative = !negative;
    s = s.slice(1);
  } else if (s.startsWith("+")) {
    s = s.slice(1);
  }
  if (s === "" || !/^\d[\d,.]*$/.test(s)) return null;

  const commas = (s.match(/,/g) ?? []).length;
  const dots = (s.match(/\./g) ?? []).length;
  const lastSep = Math.max(s.lastIndexOf(","), s.lastIndexOf("."));

  let decimalAt = -1;
  if (lastSep !== -1) {
    const after = s.slice(lastSep + 1);
    if (!/^\d+$/.test(after)) return null;
    const sepChar = s[lastSep];
    const repeated = sepChar === "," ? commas > 1 : dots > 1;
    if (commas > 0 && dots > 0) decimalAt = lastSep;
    else if (repeated) decimalAt = -1;
    else if (after.length === 3) decimalAt = sepChar === "." ? lastSep : -1;
    else decimalAt = lastSep;
  }

  const intPart = (decimalAt === -1 ? s : s.slice(0, decimalAt)).replace(
    /[,.]/g,
    "",
  );
  const fracPart = decimalAt === -1 ? "" : s.slice(decimalAt + 1);
  if (!/^\d*$/.test(intPart) || !/^\d*$/.test(fracPart)) return null;
  if (intPart === "" && fracPart === "") return null;

  const value = Number(`${intPart || "0"}.${fracPart || "0"}`);
  if (!Number.isFinite(value)) return null;
  return {
    value: negative ? -value : value,
    // Money is written to at least 2 places; keep more when the source had more.
    decimals: Math.max(2, fracPart.length),
  };
}

/**
 * Parse an amount written in any of the formats banks export: thousands
 * separators, decimal commas, parenthesised or trailing negatives, currency
 * symbols. Returns `null` when the value isn't a number at all.
 *
 * Thin over {@link parseAmount}, kept because it is the named, tested
 * "is this a number, and which one" question callers actually ask.
 */
export function normalizeAmount(raw: string): number | null {
  return parseAmount(raw)?.value ?? null;
}

const MONTHS: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

export function isoDate(year: number, month: number, day: number): { date: string } | { error: string } {
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return { error: "out-of-range date" };
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return { date: `${year}-${pad(month)}-${pad(day)}` };
}

/**
 * The date part of a cell that carries more than a date.
 *
 * Some exports append a second date or a time: Schwab writes
 * `04/17/2023 as of 04/15/2023` (posted date, then effective date) and Revolut
 * writes `2026-08-01 09:12:33`. Keep the *leading* date — it is what Schwab's
 * own history screen sorts by, so it is what a reader sees when comparing the
 * output against the site, and truncating a suffix needs no guess about which
 * of the two dates the row belongs to.
 *
 * Cutting at the first ` as of ` or `HH:MM` — rather than enumerating the
 * tails that may follow one — is what makes fractional seconds, UTC offsets
 * and spelled-out timezones (`09:12:33.123`, `09:12:33+05:30`,
 * `02:21:05 PM Eastern`) fall out for free. No date format loses anything:
 * a date holds no colon, and the time branch needs a separator before it, so
 * month-name forms (`1 Aug 2026`, `Aug 1, 2026`) and a cell that is *only* a
 * time (`09:12:33`, which must still fail) are left exactly as they are.
 *
 * Shared by {@link normalizeDate} and {@link detectDateOrder} because it
 * describes the cell, not one reading of it: a column the first tolerates and
 * the second does not would have its day/month order guessed silently.
 */
function dateToken(raw: string): string {
  return (raw ?? "").trim().replace(/(\s+as\s+of\b|[\sT]+\d{1,2}:\d{2}).*$/i, "");
}

/**
 * Parse a date into Beancount's `YYYY-MM-DD`, keeping the machine-readable
 * failure kind alongside the English text. Two-part numeric dates are read
 * according to `order` — see {@link detectDateOrder}, which infers it from the
 * whole column so the UI only has to ask when it's genuinely ambiguous.
 *
 * Converters report date failures as `import.unreadable-date` notes, which
 * need the `kind`; string-only callers (CSV's `RowError` schema) use
 * {@link normalizeDate}, which returns the same English text as always.
 */
export function normalizeDateDetailed(
  raw: string,
  order: DateOrder = "mdy",
): { date: string } | { error: string; kind: DateErrorKind } {
  const s = dateToken(raw);
  if (s === "") return { error: "empty date", kind: "empty" };

  let m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(s);
  if (m) return withDateKind(isoDate(+m[1], +m[2], +m[3]));

  m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/.exec(s);
  if (m) {
    const year = m[3].length === 2 ? 2000 + +m[3] : +m[3];
    const [a, b] = [+m[1], +m[2]];
    // A value over 12 settles the order regardless of the caller's guess.
    if (a > 12) return withDateKind(isoDate(year, b, a));
    if (b > 12) return withDateKind(isoDate(year, a, b));
    return withDateKind(order === "dmy" ? isoDate(year, b, a) : isoDate(year, a, b));
  }

  m = /^(\d{1,2})[\s-]([A-Za-z]{3,})[\s-](\d{4})$/.exec(s);
  if (m) {
    const month = MONTHS[m[2].slice(0, 3).toLowerCase()];
    if (!month) return { error: "unknown month", kind: "unknown-month" };
    return withDateKind(isoDate(+m[3], month, +m[1]));
  }

  m = /^([A-Za-z]{3,})\s+(\d{1,2}),?\s+(\d{4})$/.exec(s);
  if (m) {
    const month = MONTHS[m[1].slice(0, 3).toLowerCase()];
    if (!month) return { error: "unknown month", kind: "unknown-month" };
    return withDateKind(isoDate(+m[3], month, +m[2]));
  }

  return { error: "unrecognized date format", kind: "unrecognized" };
}

/** `isoDate`'s only failure is an out-of-range component. */
function withDateKind(
  result: { date: string } | { error: string },
): { date: string } | { error: string; kind: DateErrorKind } {
  return "date" in result ? result : { error: result.error, kind: "out-of-range" };
}

/**
 * Parse a date into Beancount's `YYYY-MM-DD`. Two-part numeric dates are read
 * according to `order` — see {@link detectDateOrder}, which infers it from the
 * whole column so the UI only has to ask when it's genuinely ambiguous.
 */
export function normalizeDate(
  raw: string,
  order: DateOrder = "mdy",
): { date: string } | { error: string } {
  const parsed = normalizeDateDetailed(raw, order);
  return "date" in parsed ? parsed : { error: parsed.error };
}

/**
 * Infer whether a date column is `MM/DD` or `DD/MM` from the whole column: any
 * value with a first part over 12 proves day-first, any with a second part
 * over 12 proves month-first.
 *
 * Three outcomes, and the difference between the last two matters to callers:
 * a `DateOrder` when the column settles it, `"ambiguous"` when it holds
 * two-part dates that genuinely cannot be told apart (the only case where a UI
 * should ask the reader), and `null` when the question never arises because
 * every date is unambiguous already — ISO, or written with a month name.
 */
export function detectDateOrder(
  values: string[],
): DateOrder | "ambiguous" | null {
  let sawDayFirst = false;
  let sawMonthFirst = false;
  let dotted = 0;
  let slashed = 0;
  let twoPart = 0;
  for (const value of values) {
    const m = /^(\d{1,2})([-/.])(\d{1,2})[-/.](\d{2}|\d{4})$/.exec(
      dateToken(value),
    );
    if (!m) continue;
    twoPart++;
    if (+m[1] > 12) sawDayFirst = true;
    if (+m[3] > 12) sawMonthFirst = true;
    if (m[2] === ".") dotted++;
    else if (m[2] === "/") slashed++;
  }
  if (twoPart === 0) return null;
  if (sawDayFirst && !sawMonthFirst) return "dmy";
  if (sawMonthFirst && !sawDayFirst) return "mdy";
  if (sawDayFirst && sawMonthFirst) return "ambiguous";
  // Nothing in the numbers settles it, so fall back to the separator: dotted
  // dates (`01.08.2026`) are a European convention and effectively always
  // day-first, while `01/08/2026` is genuinely ambiguous worldwide.
  if (dotted > 0 && slashed === 0) return "dmy";
  return "ambiguous";
}
